// What the Assessment and Training flows share beyond Details: the
// capabilities grid, Elliott's preview, and Review (the preview, then every
// other input summarised, each editable in place).

import React, { useEffect, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DosenTooltip from '../../../../../components/ui/DosenTooltip';
import ElliottWorking from '../../../../../components/ui/ElliottWorking';
import { formatDate as formatHouseDate } from '../../../../../utils/formatDate';
import { apiMessage } from '../../../../../api/capability';
import type { SetupCapability, SetupRole } from '../capabilitySetup';
import { sourceOf, type CapabilitySource } from '../capabilitySource';
import { countedCapabilitiesOf, isRoleUsable, type CapabilitySet } from '../sharedSets';
import SourceChip from '../SourceChip';
import {
  ActionButton,
  BODY,
  BRANDY,
  ChoiceGrid,
  DANGER,
  FONT,
  FormRow,
  GridChoice,
  LABEL,
  LINE,
  RowGroupLabel,
  TEXT,
  TEXT_3,
  TEXT_4,
  TEXTAREA_SX
} from '../designSystem';
import type { FlowDetails, FlowKind } from './DetailsStep';

export const listNames = (names: string[]) =>
  names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
/** The house date ("Jun 30, 2026"), or null with no date. */
export const formatDate = (d: Date | null) => (d ? formatHouseDate(d) : null);

/**
 * The roles whose capabilities a program covers: the ones chosen; with none
 * chosen, every role that has some. Either kind only takes roles that are
 * ready, each with what counts on it: its own capabilities that count (one
 * added since its levels were last confirmed waits for them to be confirmed
 * again) and the shared ones applied to it (the team never confirms those, but
 * confirms the role).
 */
export const rolesInScope = (
  _kind: FlowKind,
  roles: SetupRole[],
  details: FlowDetails,
  sets: CapabilitySet[] = []
): SetupRole[] => {
  const usable = roles
    .filter((r) => isRoleUsable(r, sets))
    .map((r) => ({ ...r, capabilities: countedCapabilitiesOf(r, sets) }))
    .filter((r) => r.capabilities.length > 0);
  const picked = usable.filter((r) => details.roleIds.includes(r.id));
  return details.allLearners || picked.length === 0 ? usable : picked;
};

const SOURCE_ORDER: CapabilitySource[] = ['role', 'development', 'standards'];
/** A role's capabilities by source, in the order Role, Development, Standards, leaving out a source with none. */
const bySource = (capabilities: SetupCapability[]): [CapabilitySource, SetupCapability[]][] =>
  SOURCE_ORDER.map((source): [CapabilitySource, SetupCapability[]] => [
    source,
    capabilities.filter((c) => sourceOf(c) === source)
  ]).filter(([, list]) => list.length > 0);

/** The capabilities to choose from, role by role, laid out in even columns. */
export const CapabilitiesField: React.FC<{
  roles: SetupRole[];
  picked: string[];
  onChange: (ids: string[]) => void;
  tip: string;
  /** Whether at least one must be chosen. A Training can be built with none, on learning outcomes alone. */
  required?: boolean;
  /** The field's name, "Capabilities" unless the flow asks its own question. */
  label?: string;
  /** A line under the name, when the flow has one. */
  help?: string;
  /** Always head the grid with its source pill (Role, Development, Standards), even when there is only the one. */
  showSource?: boolean;
}> = ({ roles, picked, onChange, tip, required = true, label = 'Capabilities', help, showSource = false }) => {
  // Each capability once: one several roles share (one id) shows under each of them, and is one choice.
  const all = roles.flatMap((r) => r.capabilities).filter((c, i, list) => list.findIndex((x) => x.id === c.id) === i);
  const toggle = (c: SetupCapability) =>
    onChange(picked.includes(c.id) ? picked.filter((id) => id !== c.id) : [...picked, c.id]);
  return (
    <FormRow
      required={required && all.length > 0}
      label={label}
      tip={tip}
      aside={
        all.length > 0 && (
          <Typography sx={{ ...LABEL, color: TEXT_3 }}>
            {picked.filter((id) => all.some((c) => c.id === id)).length} of {all.length} Selected
          </Typography>
        )
      }
    >
      {help && all.length > 0 && <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>{help}</Typography>}
      {all.length === 0 && (
        <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>
          The roles in this audience have no capabilities yet. Add capabilities for these roles in Capabilities.
        </Typography>
      )}
      {roles.map((r, i) => (
        <Box
          key={r.id}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            ...(i > 0 ? { pt: '10px', borderTop: `1px solid ${LINE}` } : {})
          }}
        >
          {roles.length > 1 && <RowGroupLabel>{r.name}</RowGroupLabel>}
          {/* Grouped by where each comes from (the role's own, Development, Standards), when there is more than its own. */}
          {bySource(r.capabilities).map(([source, list]) => (
            <Box key={source} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {(showSource || r.capabilities.some((c) => sourceOf(c) !== 'role')) && (
                <Box>
                  <SourceChip source={source} />
                </Box>
              )}
              <ChoiceGrid columns={3}>
                {list.map((c) => (
                  <GridChoice
                    key={c.id}
                    label={c.name}
                    tip={c.criteria.capable ? `Capable: ${c.criteria.capable}` : c.name}
                    selected={picked.includes(c.id)}
                    onClick={() => toggle(c)}
                  />
                ))}
              </ChoiceGrid>
            </Box>
          ))}
        </Box>
      ))}
    </FormRow>
  );
};

// ---------------------------------------------------------------------------
// Elliott's preview
// ---------------------------------------------------------------------------

export interface ElliottPreview {
  text: string | null;
  /** Being written, or about to be: asked for, with no text yet and no failure. */
  writing: boolean;
  /** Why the last preview couldn't be written, when it couldn't. */
  error: string | null;
  setText: (text: string) => void;
  regenerate: () => void;
}

/**
 * Elliott's preview of the program, written by the backend from the inputs
 * (`write`). Written when `active` first becomes true, and again on Regenerate.
 * Given one already written (`kept`, from a program reopened), it starts on that.
 */
export const useElliottPreview = (
  write: () => Promise<string>,
  active: boolean,
  kept: string | null = null
): ElliottPreview => {
  const [text, setText] = useState<string | null>(kept);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const regenerate = () => {
    setWriting(true);
    setError(null);
    write()
      .then((written) => alive.current && setText(written))
      .catch((err) => alive.current && setError(apiMessage(err, 'Elliott could not write the preview.')))
      .finally(() => alive.current && setWriting(false));
  };
  useEffect(() => {
    if (active && text === null && !writing) regenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  // Asked for, with nothing written and no failure, it is as good as being written: so it reads from
  // the first render, before the effect above starts it, and nothing can go ahead without it.
  const pending = active && text === null && error === null;
  return { text, writing: writing || pending, error, setText, regenerate };
};

// ---------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------

/** Elliott's preview: Elliott's working state while it is written, then the text to edit, with Regenerate Preview. */
const PreviewRow: React.FC<{ preview: ElliottPreview }> = ({ preview }) => (
  <FormRow
    label="Preview"
    tip="Elliott's summary, written from what you entered. Edit it freely: it becomes the program's description."
    aside={
      preview.text !== null &&
      !preview.writing && (
        <DosenTooltip
          title="Regenerate the preview. Elliott uses this preview to inform the program's structure and content generation."
          placement="top"
        >
          <IconButton
            aria-label="Regenerate Preview"
            onClick={preview.regenerate}
            sx={{ width: 30, height: 30, p: 0, color: TEXT_3, '&:hover': { color: TEXT } }}
          >
            <AutorenewIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DosenTooltip>
      )
    }
  >
    {preview.text === null && preview.error && !preview.writing ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '6px' }}>
        <Typography role="alert" sx={{ ...BODY, flex: 1, color: DANGER }}>
          {preview.error}
        </Typography>
        <ActionButton onClick={preview.regenerate}>Try Again</ActionButton>
      </Box>
    ) : preview.writing || preview.text === null ? (
      <Box sx={{ py: '10px' }}>
        <ElliottWorking message="Elliott is writing the preview..." size="inline" />
      </Box>
    ) : (
      <Box
        component="textarea"
        rows={4}
        value={preview.text}
        aria-label="Preview"
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => preview.setText(e.target.value)}
        sx={TEXTAREA_SX}
      />
    )}
  </FormRow>
);

export interface SummaryLine<K extends string> {
  /** Which input Edit opens. Several lines can share one (Owner and Publish By). */
  key: K;
  label: string;
  value: string | null;
}

/**
 * Review: Elliott's preview (editable, with Regenerate), then every other
 * input on a line of its own. Edit opens that one input over this screen, so
 * the manager changes it without leaving Review.
 */
export const ReviewFields = <K extends string>({
  preview,
  lines,
  renderEditor
}: {
  /** Elliott's write-up, above the summary. Left out where the flow has no preview at this point. */
  preview?: ElliottPreview;
  lines: SummaryLine<K>[];
  /** The input for a key, as its step shows it. */
  renderEditor: (key: K) => React.ReactNode;
}) => {
  const [editing, setEditing] = useState<K | null>(null);
  return (
    <>
      {preview && <PreviewRow preview={preview} />}

      <FormRow label="Summary" tip="Everything you entered. Edit opens that input here, without leaving Review.">
        <Box>
          {lines.map((line, i) => (
            <Box
              key={line.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 38,
                ...(i > 0 ? { borderTop: '1px solid #1F1F1F' } : {})
              }}
            >
              <Typography sx={{ ...LABEL, width: 130, flexShrink: 0, color: TEXT_3 }}>{line.label}</Typography>
              <DosenTooltip title={line.value ?? ''} placement="top-start" enterDelay={500}>
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: FONT,
                    fontSize: 13,
                    color: line.value ? TEXT : TEXT_4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {line.value ?? 'Not set'}
                </Typography>
              </DosenTooltip>
              <Box
                component="button"
                type="button"
                aria-label={`Edit ${line.label}`}
                onClick={() => setEditing(line.key)}
                sx={{
                  flexShrink: 0,
                  height: 38,
                  pl: '12px',
                  border: 'none',
                  bgcolor: 'transparent',
                  color: TEXT_3,
                  fontFamily: FONT,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': { color: BRANDY }
                }}
              >
                Edit
              </Box>
            </Box>
          ))}
        </Box>
      </FormRow>

      {/* The one input being edited, over Review. */}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 720,
            maxWidth: '92vw',
            maxHeight: '86vh',
            bgcolor: '#1C1C1C',
            border: `1px solid ${LINE}`,
            borderRadius: '8px',
            backgroundImage: 'none',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '20px', pt: '16px' }}>
          {editing !== null && renderEditor(editing)}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            p: '12px 20px',
            borderTop: `1px solid ${LINE}`
          }}
        >
          <ActionButton primary onClick={() => setEditing(null)}>
            Done
          </ActionButton>
        </Box>
      </Dialog>
    </>
  );
};
